/**
 * perfLog — lightweight, opt-in performance instrumentation for the
 * Public Viewer. Logs to console.info (namespaced `[perf]`) and stashes
 * the latest values on `window.__viewerPerf` so the ViewerDebugPanel can
 * surface them inline.
 *
 * Enabled when ANY of:
 *   - URL contains `?debug=1` or `?perf=1`
 *   - `localStorage.token` is set (admin/preview sessions)
 *   - `localStorage.viewer_perf === '1'` (manual opt-in)
 */

function enabled() {
  if (typeof window === "undefined") return false;
  if (window.__viewerPerfForced === false) return false;
  if (window.__viewerPerfForced === true) return true;
  try {
    const qs = window.location.search || "";
    if (/[?&](debug|perf|diag)=1\b/.test(qs)) return true;
    if (localStorage.getItem("token")) return true;
    if (localStorage.getItem("viewer_perf") === "1") return true;
  } catch { /* ignore — SSR / privacy */ }
  return false;
}

function ensureStore() {
  if (typeof window === "undefined") return null;
  if (!window.__viewerPerf) window.__viewerPerf = { marks: {}, measures: {} };
  return window.__viewerPerf;
}

/** Stamp a named moment. Repeat marks overwrite — most recent wins. */
export function perfMark(label) {
  if (!enabled()) return;
  const store = ensureStore();
  if (!store) return;
  store.marks[label] = performance.now();
}

/** Measure ms between two marks. Returns the duration, also stashed in store.measures[name]. */
export function perfMeasure(name, startLabel, endLabel = null) {
  if (!enabled()) return null;
  const store = ensureStore();
  if (!store) return null;
  const s = store.marks[startLabel];
  const e = endLabel ? store.marks[endLabel] : performance.now();
  if (s == null || e == null) return null;
  const ms = e - s;
  store.measures[name] = ms;
  // eslint-disable-next-line no-console
  console.info(`[perf] ${name}: ${ms.toFixed(1)}ms`);
  return ms;
}

/** One-shot record (no start/end marks). Useful for "this just happened, log it". */
export function perfRecord(name, ms, extra = null) {
  if (!enabled()) return;
  const store = ensureStore();
  if (!store) return;
  store.measures[name] = ms;
  // eslint-disable-next-line no-console
  console.info(`[perf] ${name}: ${ms.toFixed(1)}ms`, extra || "");
}

/** Read a response's Server-Timing + X-Cache headers and log them. */
export function perfFromHeaders(label, response) {
  if (!enabled() || !response?.headers) return;
  try {
    const cache = response.headers.get?.("x-cache") || response.headers["x-cache"];
    const timing = response.headers.get?.("server-timing") || response.headers["server-timing"];
    // eslint-disable-next-line no-console
    console.info(`[perf] ${label}: cache=${cache || "?"} server-timing=${timing || "?"}`);
  } catch { /* ignore */ }
}

/** Read all recorded measurements (for ViewerDebugPanel). */
export function perfSnapshot() {
  const store = ensureStore();
  return store?.measures || {};
}

// ---------------------------------------------------------------------------
// Strict fallback logging — used whenever the viewer degrades from a smooth
// scrub transition to a plain fade. EVERY fade fallback in PublicViewer/
// ScrubOverlay MUST log via `logFallback` with one of the reasons below so
// QA can verify that no fade ever happens silently.
// ---------------------------------------------------------------------------
export const FALLBACK_REASONS = Object.freeze({
  MISSING_TRANSITION_MEDIA:    "missing_transition_media",
  TRANSITION_NOT_READY:        "transition_not_ready",
  PLAYBACK_ERROR:              "playback_error",
  UNSUPPORTED_SCRUB:           "unsupported_scrub",
  NO_TRANSITION_FOR_DIRECTION: "no_transition_for_direction",
  MANUAL_FALLBACK:             "manual_fallback",
});

const REASON_VALUES = new Set(Object.values(FALLBACK_REASONS));

/**
 * Log a fade fallback with a strict reason enum. Always writes to
 * `window.__viewerPerf.fallbacks` (array of {ts, reason, details}) and
 * `console.warn` so it is visible even without ?debug=1.
 */
export function logFallback(reason, details = {}) {
  if (!REASON_VALUES.has(reason)) {
    // eslint-disable-next-line no-console
    console.error(`[fallback] INVALID reason="${reason}" — must be one of ${[...REASON_VALUES].join("|")}`);
    return;
  }
  const store = ensureStore() || { fallbacks: [] };
  if (!store.fallbacks) store.fallbacks = [];
  const entry = { ts: performance.now(), reason, details };
  store.fallbacks.push(entry);
  // Keep only the last 50 entries to avoid unbounded growth.
  if (store.fallbacks.length > 50) store.fallbacks.splice(0, store.fallbacks.length - 50);
  // eslint-disable-next-line no-console
  console.warn(`[fallback] ${reason}`, details);
}

/** Log a video.play() promise result so denied-autoplay is never silent. */
export function logPlayResult(label, ok, err = null) {
  if (!enabled()) return;
  const store = ensureStore();
  if (!store) return;
  if (!store.playResults) store.playResults = [];
  const entry = {
    ts: performance.now(),
    label,
    ok,
    name: err?.name || null,
    message: err?.message || null,
  };
  store.playResults.push(entry);
  if (store.playResults.length > 30) store.playResults.splice(0, store.playResults.length - 30);
  // eslint-disable-next-line no-console
  console.info(`[play] ${label} → ${ok ? "ok" : "denied"}`, err ? { name: err.name, message: err.message } : "");
}

// ---------------------------------------------------------------------------
// P2 — Preload / readiness telemetry
//
// `recordPrewarm(id, status, ms)` is called by the hidden-video prewarm hook
// every time a transition video either becomes `canplay` (status='hit') or
// fails to do so before user release (status='miss'). The aggregate hit
// rate is the single best indicator of whether scrub transitions will feel
// instant on the user's device + network.
// ---------------------------------------------------------------------------
export function recordPrewarm(id, status, ms = null, extra = {}) {
  if (!enabled()) return;
  const store = ensureStore();
  if (!store) return;
  if (!store.prewarms) store.prewarms = {};
  store.prewarms[id] = { status, ms, ts: performance.now(), ...extra };
  // eslint-disable-next-line no-console
  console.info(`[prewarm] ${id} ${status}${ms != null ? ` ${ms.toFixed(0)}ms` : ""}`);
}

/** Roll up the prewarm table into a hit-rate summary. */
export function prewarmStats() {
  const store = ensureStore();
  const table = store?.prewarms || {};
  const entries = Object.values(table);
  const hits = entries.filter((e) => e.status === "hit").length;
  const total = entries.length;
  return {
    total,
    hits,
    misses: total - hits,
    hitRate: total ? hits / total : null,
  };
}

/** Roll up fallback events into a per-reason histogram. */
export function fallbackHistogram() {
  const store = ensureStore();
  const arr = store?.fallbacks || [];
  const out = {};
  for (const f of arr) out[f.reason] = (out[f.reason] || 0) + 1;
  return out;
}

/**
 * One-shot diagnostic snapshot — bundle every signal the debug HUD has into
 * a single JSON-serialisable object. Used by the "Copy diagnostic" button
 * so QA can paste the entire viewer state into a bug report without
 * scraping the console.
 */
/** Snapshot of device + live foreground-video state — the fields that matter
 * most for diagnosing iOS Safari autoplay / open-failure issues. */
function deviceInfo() {
  if (typeof window === "undefined") return {};
  const nav = window.navigator || {};
  let video = null;
  try {
    const v = document.querySelector('[data-testid="scene-media-video"]');
    if (v) {
      video = {
        paused: v.paused, muted: v.muted, loop: v.loop, autoplay: v.autoplay,
        readyState: v.readyState, networkState: v.networkState,
        currentTime: Number((v.currentTime || 0).toFixed(2)),
        duration: isFinite(v.duration) ? Number(v.duration.toFixed(2)) : null,
        videoWidth: v.videoWidth, videoHeight: v.videoHeight,
        src: (v.currentSrc || v.src || "").slice(0, 160),
        error: v.error ? { code: v.error.code, message: v.error.message } : null,
      };
    }
  } catch { /* ignore */ }
  let standalone = false;
  try {
    standalone = nav.standalone === true ||
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  } catch { /* ignore */ }
  return {
    ua: nav.userAgent || null,
    platform: nav.platform || null,
    language: nav.language || null,
    dpr: window.devicePixelRatio || 1,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    visualViewport: window.visualViewport
      ? { w: Math.round(window.visualViewport.width), h: Math.round(window.visualViewport.height), scale: window.visualViewport.scale }
      : null,
    orientation: (screen.orientation && screen.orientation.type) || null,
    standalone,
    online: nav.onLine,
    connection: nav.connection ? {
      effectiveType: nav.connection.effectiveType,
      downlink: nav.connection.downlink,
      rtt: nav.connection.rtt,
      saveData: nav.connection.saveData,
    } : null,
    video,
    tapToBeginVisible: !!document.querySelector('[data-testid="viewer-tap-to-begin"]'),
  };
}

export function perfDiagnostic() {
  const store = ensureStore() || {};
  const measures = store.measures || {};
  // Surface scene canplay times in a friendly shape.
  const sceneCanplays = Object.fromEntries(
    Object.entries(measures)
      .filter(([k]) => k.startsWith("scene:") && k.endsWith(":canplay"))
      .map(([k, v]) => [k.slice("scene:".length, -":canplay".length), v])
  );
  return {
    capturedAt: new Date().toISOString(),
    url: typeof window !== "undefined" ? window.location.href : null,
    ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
    device: deviceInfo(),
    fallbackHistogram: fallbackHistogram(),
    fallbacks: (store.fallbacks || []).slice(-20),
    playResults: (store.playResults || []).slice(-20),
    prewarmStats: prewarmStats(),
    prewarms: store.prewarms || {},
    measures,
    sceneCanplays,
  };
}
